import { useMemo } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { v4 as uuidv4 } from "uuid"
import { authClient } from "@/lib/auth-client"
import { trpc } from "@/lib/trpc-client"
import { useRealtimeInvalidate } from "@/lib/hooks/use-realtime-invalidate"
import { showToast } from "@/lib/lazy-toast"

// Shared across every surface that renders ExploreCards (explore, home,
// dashboard). They all read the same `["guides","public"]` query cache, so the
// optimistic like_count bump below shows up on whichever page is mounted.
const PUBLIC_GUIDES_KEY = [`guides`, `public`] as const
const MY_LIKES_KEY = [`guides`, `myLikes`] as const
type MyLike = Awaited<ReturnType<typeof trpc.guides.myLikes.query>>[number]
type PublicGuide = Awaited<ReturnType<typeof trpc.guides.listPublic.query>>[number]

export function useGuideLikes() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: session } = authClient.useSession()
  const { data: guideLikes = [], refetch: refetchLikes } = useQuery({
    queryKey: MY_LIKES_KEY,
    queryFn: () => trpc.guides.myLikes.query(),
    enabled: !!session,
  })

  // Realtime: when anyone likes/unlikes a guide, a row is inserted/deleted in
  // guide_likes — refetch the public list so the like_count badge updates live
  // for every viewer (not just the person who clicked). The signal only says
  // "something changed"; the refetch goes through tRPC.
  // Gated on session so anonymous page views don't open a WebSocket that can't
  // usefully connect without auth (also avoids console errors in crawlers).
  useRealtimeInvalidate(`guide_likes`, PUBLIC_GUIDES_KEY, !!session)
  // Also refresh the caller's own likes so a guide liked in one tab flips to
  // "Unlike" in their other tabs — they shouldn't be able to like it again.
  useRealtimeInvalidate(`guide_likes`, MY_LIKES_KEY, !!session)

  // Optimistically nudge a guide's like_count in the cached public list. The
  // realtime refetch above replaces it with the authoritative server value, so
  // we edit the same field rather than layering a separate delta (which would
  // double-count once the list refetched).
  const adjustLikeCount = (guideId: string, delta: number) =>
    queryClient.setQueryData<PublicGuide[]>(PUBLIC_GUIDES_KEY, (old) =>
      old?.map((g) =>
        g.id === guideId
          ? { ...g, like_count: Math.max(0, (g.like_count ?? 0) + delta) }
          : g
      )
    )

  const likedGuideIds = useMemo(
    () => new Set(guideLikes.map((l) => l.guide_id)),
    [guideLikes]
  )
  const likeByGuideId = useMemo(
    () => new Map(guideLikes.map((l) => [l.guide_id, l.id])),
    [guideLikes]
  )

  const handleLikeGuide = async (guideId: string) => {
    if (!session) {
      navigate({ to: `/login` })
      return
    }
    // Already liked (e.g. liked in another tab) — don't double-count.
    if (likeByGuideId.has(guideId)) return
    const likeId = uuidv4()
    // Optimistic: add to the likes cache + bump the visible count.
    queryClient.setQueryData<MyLike[]>(MY_LIKES_KEY, (old) => [
      ...(old ?? []),
      { id: likeId, guide_id: guideId },
    ])
    adjustLikeCount(guideId, 1)
    try {
      await trpc.guides.like.mutate({ id: guideId, likeId })
    } catch (error) {
      console.error(`Failed to like guide:`, error)
      adjustLikeCount(guideId, -1)
      refetchLikes()
      void showToast({
        title: `Couldn't like this guide`,
        description: `Something went wrong on our end. Please try again.`,
        color: `danger`,
      })
    }
  }

  const handleUnlikeGuide = async (guideId: string) => {
    if (!likeByGuideId.has(guideId)) return
    queryClient.setQueryData<MyLike[]>(MY_LIKES_KEY, (old) =>
      (old ?? []).filter((l) => l.guide_id !== guideId)
    )
    adjustLikeCount(guideId, -1)
    try {
      await trpc.guides.unlike.mutate({ id: guideId })
    } catch (error) {
      console.error(`Failed to unlike guide:`, error)
      adjustLikeCount(guideId, 1)
      refetchLikes()
      void showToast({
        title: `Couldn't unlike this guide`,
        description: `Something went wrong on our end. Please try again.`,
        color: `danger`,
      })
    }
  }

  return { likedGuideIds, handleLikeGuide, handleUnlikeGuide }
}
